import { observable, computed, action } from 'mobx'

/**
 * Computes the tabs. Return `false` for a tab that should be hidden.
 */
export type GetTabs<T> = () => (TabSpec<T> | false)[]

/**
 * Tab specification.
 */
export interface TabSpec<T> {
  title: string
  tab: T
  hasError: () => boolean
}

/**
 * Creates a tab spec that, unless given `hasError`, never shows an error.
 */
export function createTabSpec<T>(
  tab: T,
  title: string,
  hasError?: () => boolean,
): TabSpec<T> {
  return {
    tab,
    title,
    hasError: hasError ?? (() => false),
  }
}

/**
 * Tracks the selected tab, falling back to the first tab when the selected
 * one is hidden. `T` is the tab identifier type.
 */
export class TabController<T> {
  @observable private accessor _currentTabId: T | null = null

  constructor(private readonly _getTabs: GetTabs<T>) {}

  /**
   * The currently selected tab.
   */
  @computed
  get current() {
    const tab = this.tabs.find((t) => t.tab === this._currentTabId)
    return tab ?? this.tabs[0]
  }

  /**
   * The available tabs.
   */
  @computed
  get tabs() {
    return this._getTabs().filter((x): x is TabSpec<T> => x !== false)
  }

  /**
   * Selects the tab.
   */
  @action.bound
  change(tabId: T) {
    this._currentTabId = tabId
  }
}
